import { createRootRoute, Outlet, redirect } from "@tanstack/react-router";
import { Toaster } from "@wondermaps/ui";
import { detectTenant } from "../hooks/use-tenant";
import { resolveActiveMap } from "../lib/resolve-active-map";
import { resolveOrgFromHostname } from "../lib/resolve-org";
import { EMPTY_VISITOR_SEARCH } from "../lib/visitor-search";

/** Routes that exist at root level and should NOT be treated as mapSlug */
const SYSTEM_ROUTES = new Set([
  "login", "signup", "superadmin", "create-org",
  "forgot-password", "reset-password", "join",
  "privacy", "account", "help",
]);

export const Route = createRootRoute({
  beforeLoad: async ({ location }) => {
    if (typeof window === "undefined") return;
    const tenant = detectTenant();

    // Only act on subdomain/custom_domain mode
    if (tenant.mode !== "subdomain" && tenant.mode !== "custom_domain") return;

    const segments = location.pathname.split("/").filter(Boolean);
    const firstSegment = segments[0] ?? "";

    // System routes stay as-is
    if (SYSTEM_ROUTES.has(firstSegment)) return;

    // Non-root paths fall through to route handlers:
    // - /{mapSlug} → $mapSlug.tsx
    // - /{orgSlug}/{mapSlug} → $orgSlug.$mapSlug.tsx (will 301 → /{mapSlug})
    // - /{orgSlug}/admin/... → admin routes (unchanged)
    if (segments.length > 0) return;

    // Bare root (/) → resolve active map, redirect to /{mapSlug} directly (one hop)
    let orgSlug = tenant.orgSlug;
    if (!orgSlug) {
      orgSlug = await resolveOrgFromHostname(window.location.hostname);
    }
    const activeMapSlug = await resolveActiveMap(orgSlug);
    throw redirect({
      to: "/$mapSlug",
      params: { mapSlug: activeMapSlug },
      search: EMPTY_VISITOR_SEARCH,
    });
  },
  component: RootLayout,
});

function RootLayout() {
  return (
    <>
      <Outlet />
      <Toaster />
    </>
  );
}
